import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { MatSnackBar } from "@angular/material/snack-bar";
import { map, takeUntil } from "rxjs/operators";
import { Subject } from "rxjs";
import { GridActionsService } from "../../../../data-grid/grid-actions.service";
import { ValidatorDecimalInput } from "../../../../directives/decimal-number-validator";
import { RepriceListItem, SkuDetail } from "../../../../models/priceyard.model";
import { PriceyardService } from "../../../priceyard/services/priceyard.service";
import { SnackbarComponent } from '../../../../components/snackbar/snackbar.component';

@Component({
  selector: "app-single-sku-price-settings",
  templateUrl: "./single-sku-price-settings.component.html",
  styleUrls: ["./single-sku-price-settings.component.scss"],
})
export class SingleSkuPriceSettingsComponent implements OnInit, OnDestroy {
  @Input() sku: SkuDetail;
  @Input() selectedMarkupType: any[] = [];
  @Output() viewEvent: EventEmitter<boolean> = new EventEmitter();

  priceForm: FormGroup;
  settingsForm: FormGroup;

  repricingModels;
  response: any = [];
  disableAutoFields = [
    "Cost",
    "Markup",
    "Min Markup",
    "Default Price",
    "Prep Cost",
    "Ship Cost",
  ];

  private destroy$ = new Subject<void>();
  isLoading: any = false;
  markupType: FormControl = new FormControl("1");
  isSidePanel: boolean;
  constructor(
    private priceyardService: PriceyardService,
    private snackBar: MatSnackBar,
    private gridActionsService: GridActionsService
  ) {
    this.isSidePanel = localStorage.getItem("toolPanel") === "true";
  }

  ngOnInit(): void {
    this.markupType.setValue(this.selectedMarkupType[0]?.value.toString() || '1');
    this.sku.priceBreakdown.forEach((price) => {
      if (price.value % 1)  price.value = Number((price.value).toFixed(2));      
    });
    this.createForm();
    this.getPricingModels();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  createForm() {
    this.priceForm = new FormGroup({});
    this.sku.priceBreakdown = this.sku.priceBreakdown.filter(
      (res) => res.name !== "Estimated Fees"
    );
    this.sku.priceBreakdown.forEach((price) => {
      this.priceForm.addControl(
        price.name,
        new FormGroup({
          value: new FormControl(
            price.name === "Markup" ? price.value * 100 : price.value,
            [Validators.min(0), Validators.max(999999), ValidatorDecimalInput]
          ),
          configType: new FormControl(
            price.name === "Map Price" && this.sku.mapModel.isOn
              ? "Auto"
              : price.configType
          ),
        })
      );
    });

    this.settingsForm = new FormGroup({
      value: new FormControl(
        this.sku.repricingModel ? this.sku.repricingModel.name : ""
      ),
      isOn: new FormControl(this.sku.pricingOn),
    });

    this.setFormObservable();
  }

  resetForm() {
    this.settingsForm.patchValue(
      {
        value: this.sku.repricingModel ? this.sku.repricingModel.id : "",
        isOn: this.sku.pricingOn
      },
      { emitEvent: false, onlySelf: true }
    );
    this.sku.priceBreakdown.forEach((price) => {
      const mainControl = this.priceForm.get(price.name);
      mainControl
        .get("value")
        .patchValue(price.name === "Markup" ? price.value * 100 : price.value, {
          emitEvent: false,
          onlySelf: true,
        });
      mainControl
        .get("configType")
        .patchValue(price.configType, { emitEvent: false, onlySelf: true });
    });
  }

  getPricingModels() {
    this.priceyardService
      .GetPriceModel()
      .pipe(
        takeUntil(this.destroy$),
        map((models: any[]) =>
          models.filter(
            (model: RepriceListItem) =>
              model.marketPlace === this.sku.accountType
          )
        )
      )
      .subscribe((e) => {
        this.repricingModels = e;
        if (this.sku.repricingModel) {
          const repricingModal = this.repricingModels.find(
            (res) => res.name === this.sku.repricingModel.name
          );
          if (repricingModal) {
            this.settingsForm.patchValue({ value: repricingModal.modelId }, { emitEvent: false });
          }
        }
      });
  }

  setFormObservable() {
    this.settingsForm
      .get("value")
      .valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() =>
        this.settingsForm.patchValue({ isOn: true })
      );
    Object.keys(this.priceForm.controls).forEach((key) => {
      this.priceForm.controls[key]
        .get("value")
        .valueChanges.pipe(takeUntil(this.destroy$)).subscribe((e) =>
          this.priceForm.controls[key].get("configType").setValue("Overridden")
        );
    });
  }

  save() {
    if (!this.settingsForm.get("isOn").pristine) {
      this.priceyardService
        .UpdateSkuPricingActive(
          this.sku.sku,
          this.sku.account,
          this.settingsForm.value.isOn
        ).pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          if (!this.priceForm.touched && this.settingsForm.pristine) this.gridActionsService.refreshData();
          this.snackBar.openFromComponent(SnackbarComponent,
              {
                duration: 3000,
                horizontalPosition: 'center',
                data: {
                  title: 'Price changes saved successfully',
                  action: 'Dismiss',
                  type: 'success',
                },
              }
          );
        });
    }

    if (this.priceForm.touched || !this.settingsForm.pristine || this.markupType.touched) {
      this.isLoading = true;
      if (
        this.priceForm.value["Max Price"].value <
          this.priceForm.value["Min Price"].value &&
        this.priceForm.value["Max Price"].configType == "Overridden" &&
        this.priceForm.value["Min Price"].configType == "Overridden"
      ) {
        this.isLoading = false;
        this.snackBar.openFromComponent(SnackbarComponent,
            {
              duration: 3000,
              horizontalPosition: 'center',
              data: {
                title: 'Min Price is more than Max Price',
                action: 'Dismiss',
                type: 'info',
              },
            }
        );
      } else {
        this.savePriceForm();
      }
    }
  }

  savePriceForm() {
    const modelName = this.repricingModels.find(
      (option) =>
        option.modelId === Number(this.settingsForm.get("value").value)
    )?.name;
    const pricingOn = this.settingsForm.get("isOn").value;
    const payload = {
      sku: this.sku.sku,
      account: this.sku.account,
      fulfillmentType: this.sku.fulfillmentType,
      changingFactors: [],
      modelName,
      pricingOn,
    };
    Object.keys(this.priceForm.controls).forEach((key) => {
      if (this.priceForm.controls[key].touched) {
        const factor = this.priceForm.controls[key].value;
        const factorValue = +this.priceForm.controls[key].value.value;
        factor["factor"] = key;
        factor["value"] = key === "Markup" ? factorValue / 100 : factorValue;
        if (!this.disableAutoFields.includes(key) || key === 'Ship Cost') {
          factor["setAuto"] =
            this.priceForm.controls[key].get("configType").value ===
            "Overridden"
              ? false
              : true;
        }
        delete factor.configType;
        if (!isNaN(factor.value)) {
          payload.changingFactors.push(factor);
        }
      }
    });
    if(this.markupType.touched) payload.changingFactors.push({"value":Number(this.markupType.value),"factor":"Markup Type","setAuto":false});

    this.priceyardService.changePriceFactor([payload]).pipe(takeUntil(this.destroy$)).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response && Object.keys(response).length !== 0) {
          const item = JSON.stringify(response).toLowerCase();
          this.sku.priceBreakdown.forEach((res: any) => {
            if (
              item.indexOf(res.name.toLowerCase()) > -1 &&
              item.indexOf("not valid") > -1
            ) {
              res.hasError = true;
            } else res.hasError = false;
          });
        } else {
          this.sku.priceBreakdown.forEach((res: any) => {
            payload.changingFactors.forEach((val) => {
              if (res.name == val.factor) {
                res.value = val.value;
                res.configType = val.setAuto ? "Auto" : "Overridden";
              }
            });
            res.hasError = false;
          });
          this.snackBar.openFromComponent(SnackbarComponent,
              {
                duration: 3000,
                horizontalPosition: 'center',
                data: {
                  title: 'Price changes saved successfully',
                  action: 'Dismiss',
                  type: 'success',
                },
              }
          );
          this.view();
          this.gridActionsService.refreshData();
        }
      },
      error: (err) => {
        this.isLoading = false;
        this.snackBar.openFromComponent(SnackbarComponent,
            {
              duration: 3000,
              horizontalPosition: 'center',
              data: {
                title: 'Failed to save price changes, Please try again.',
                action: 'Dismiss',
                type: 'error',
              },
            }
        );
      }
      }
    );
  }

  view() {
    this.viewEvent.emit(true);
  }

  setValue(control,Value){
    if(this.priceForm.controls[control].get('configType').value !== Value){
      this.priceForm.controls[control].markAsTouched();
      this.priceForm.controls[control].get('configType').setValue(Value);
    }
  }
}